/**
 * Source-profile detection for Prospection import (Lot 6.6A.1).
 *
 * PARSE → SOURCE PROFILE DETECTION → DETERMINISTIC MAPPING → (optional AI)
 * → USER MAPPING REVIEW → NORMALIZATION → SERVER PREVIEW → IMPORT
 *
 * Profiles are "jlr" | "generic". Detection is deterministic (no Vertex).
 * Excel 6.6B should reuse this helper on the same header model.
 */

import { IGNORE_TARGET, IMPORT_TARGETS, normalizeHeader, suggestMapping } from "./prospectImportMapping.js";
import {
  OWNER_SLOT_ORDER,
  buildJlrMappingProfile,
  detectOwnerBlocks,
  detectRepeatedPropertyAddressBlock,
  ownerGroupHasAddressSlots,
  sanitizeOwnerGroupIndexes,
  suggestJlrColumnMapping,
} from "./prospectImportJlr.js";

export const SOURCE_PROFILES = Object.freeze(["jlr", "generic"]);
export const JLR_DETECTION_THRESHOLD = 0.55;

function hasNormalized(normalizedHeaders, value) {
  return normalizedHeaders.includes(value);
}

function hasAnyNormalized(normalizedHeaders, values) {
  return values.some((value) => normalizedHeaders.includes(value));
}

/**
 * Deterministic JLR vs generic detection from headers only.
 * Uses a score/confidence; does not require an exact column count.
 *
 * @param {string[]} headers
 * @returns {{
 *   profile: 'jlr' | 'generic',
 *   confidence: number,
 *   reasons: string[],
 *   ownerBlocks: object[],
 *   repeatedPropertyBlock: object | null,
 * }}
 */
export function detectProspectImportProfile(headers) {
  const list = Array.isArray(headers) ? headers : [];
  const normalized = list.map((header) => normalizeHeader(header));
  const reasons = [];
  let score = 0;

  if (hasNormalized(normalized, "adresse immeuble")) {
    score += 0.28;
    reasons.push("adresse_immeuble");
  }
  if (hasNormalized(normalized, "code postal immeuble")) {
    score += 0.18;
    reasons.push("code_postal_immeuble");
  }
  if (hasNormalized(normalized, "ville immeuble")) {
    score += 0.18;
    reasons.push("ville_immeuble");
  }
  if (hasAnyNormalized(normalized, ["nombre logements", "nombre de logements"])) {
    score += 0.12;
    reasons.push("nombre_logements");
  }
  if (hasAnyNormalized(normalized, ["categorie batiment", "type construction", "annee construction"])) {
    score += 0.04;
    reasons.push("building_meta");
  }

  const ownerBlocks = detectOwnerBlocks(list);
  if (ownerBlocks.length >= 1) {
    score += 0.1;
    reasons.push(`owner_blocks:${ownerBlocks.length}`);
  }
  if (ownerBlocks.length >= 2) score += 0.06;
  if (ownerBlocks.length >= 3) score += 0.04;

  const repeatedPropertyBlock = detectRepeatedPropertyAddressBlock(list, ownerBlocks);
  if (repeatedPropertyBlock) {
    score += 0.1;
    reasons.push("repeated_property_address");
  }

  const confidence = Math.min(1, Math.round(score * 100) / 100);
  const profile = confidence >= JLR_DETECTION_THRESHOLD ? "jlr" : "generic";
  return { profile, confidence, reasons, ownerBlocks, repeatedPropertyBlock };
}

function genericProfile(detection) {
  return {
    sourceProfile: "generic",
    confidence: detection.confidence,
    reasons: detection.reasons,
    fields: {},
    ownerGroups: [],
    propertyFallback: null,
  };
}

/**
 * Detect the source profile and build the mapping the user will review.
 * Generic files keep suggestMapping(); JLR uses the profile transform.
 *
 * @param {string[]} headers
 */
export function suggestImportSetup(headers) {
  const detection = detectProspectImportProfile(headers);
  if (detection.profile === "jlr") {
    const profile = buildJlrMappingProfile(headers, detection);
    return {
      detection,
      profile,
      mapping: suggestJlrColumnMapping(headers, profile),
    };
  }
  return {
    detection,
    profile: genericProfile(detection),
    mapping: suggestMapping(headers),
  };
}

export function mappingSatisfiedTargets(profile) {
  if (profile?.sourceProfile === "jlr" && profile?.fields?.province?.kind === "constant") {
    return ["province"];
  }
  return [];
}

export function isJlrProfile(profile) {
  return profile?.sourceProfile === "jlr";
}

/** Vertex / profile field targets (property columns + notes). Owner blocks use ownerGroups. */
export const VERTEX_MAPPING_FIELD_TARGETS = Object.freeze([
  "address",
  "city",
  "province",
  "postalCode",
  "numberOfUnits",
  "evaluation",
  "contrepartie",
  "derniereTransaction",
  "nomTransaction",
  "categorieBatiment",
  "typeConstruction",
  "nombreEtages",
  "anneeConstruction",
  "lotNumbers",
  "cadastralDivision",
  "municipalRollNumber",
  "landAreaValue",
  "landAreaUnit",
]);

function isMappedField(field) {
  if (!field || typeof field !== "object") return false;
  if (field.kind === "constant" && typeof field.value === "string" && field.value.trim()) return true;
  if (field.kind === "column" && Number.isInteger(field.index) && field.index >= 0) return true;
  return false;
}

function normalizeMappingArray(mapping, columnCount) {
  const next = Array.from({ length: columnCount }, (_, i) => {
    const value = Array.isArray(mapping) ? mapping[i] : IGNORE_TARGET;
    return value && IMPORT_TARGETS.includes(value) ? value : IGNORE_TARGET;
  });
  return next;
}

/**
 * Build a mapping array from a Vertex suggestion alone (blank base).
 * Prefer mergeVertexSuggestionIntoSetup / applyVertexSuggestionToSetup for apply.
 */
export function columnMappingFromVertexSuggestion(suggestion, columnCount) {
  const count = Number.isInteger(columnCount) ? columnCount : 0;
  const mapping = Array.from({ length: count }, () => IGNORE_TARGET);
  const fields = suggestion?.fields && typeof suggestion.fields === "object" ? suggestion.fields : {};
  const assign = (field, target) => {
    if (!IMPORT_TARGETS.includes(target)) return;
    if (field?.kind !== "column" || !Number.isInteger(field.index)) return;
    if (field.index < 0 || field.index >= mapping.length) return;
    const previous = mapping.indexOf(target);
    if (previous >= 0) mapping[previous] = IGNORE_TARGET;
    mapping[field.index] = target;
  };
  for (const target of VERTEX_MAPPING_FIELD_TARGETS) {
    assign(fields[target], target);
  }
  return mapping;
}

function fieldFingerprint(field) {
  if (!field || typeof field !== "object") return "unmapped";
  if (field.kind === "constant") return `constant:${field.value || ""}`;
  if (field.kind === "column" && Number.isInteger(field.index)) return `column:${field.index}`;
  return "unmapped";
}

/**
 * Count actual mapping/config differences (not "Vertex returned N fields").
 * Unit: each changed column target + each changed profile.fields entry +
 * each changed owner-group slot index.
 */
export function countMappingConfigChanges(before, after) {
  let count = 0;
  const mappingA = Array.isArray(before?.mapping) ? before.mapping : [];
  const mappingB = Array.isArray(after?.mapping) ? after.mapping : [];
  const mappingLen = Math.max(mappingA.length, mappingB.length);
  for (let i = 0; i < mappingLen; i += 1) {
    if ((mappingA[i] || IGNORE_TARGET) !== (mappingB[i] || IGNORE_TARGET)) count += 1;
  }
  const fieldsA = before?.profile?.fields && typeof before.profile.fields === "object" ? before.profile.fields : {};
  const fieldsB = after?.profile?.fields && typeof after.profile.fields === "object" ? after.profile.fields : {};
  const fieldKeys = new Set([...Object.keys(fieldsA), ...Object.keys(fieldsB)]);
  for (const key of fieldKeys) {
    if (fieldFingerprint(fieldsA[key]) !== fieldFingerprint(fieldsB[key])) count += 1;
  }
  const groupsA = Array.isArray(before?.profile?.ownerGroups) ? before.profile.ownerGroups : [];
  const groupsB = Array.isArray(after?.profile?.ownerGroups) ? after.profile.ownerGroups : [];
  const groupLen = Math.max(groupsA.length, groupsB.length);
  for (let g = 0; g < groupLen; g += 1) {
    const a = groupsA[g] && typeof groupsA[g] === "object" ? groupsA[g] : {};
    const b = groupsB[g] && typeof groupsB[g] === "object" ? groupsB[g] : {};
    const slots = new Set([...OWNER_SLOT_ORDER, ...Object.keys(a), ...Object.keys(b)]);
    for (const slot of slots) {
      const av = Number.isInteger(a[slot]) ? a[slot] : null;
      const bv = Number.isInteger(b[slot]) ? b[slot] : null;
      if (av !== bv) count += 1;
    }
  }
  return count;
}

function countFilledEmptyMappings(beforeMapping, afterMapping) {
  const before = Array.isArray(beforeMapping) ? beforeMapping : [];
  const after = Array.isArray(afterMapping) ? afterMapping : [];
  const len = Math.max(before.length, after.length);
  let filled = 0;
  for (let i = 0; i < len; i += 1) {
    const prev = before[i] || IGNORE_TARGET;
    const next = after[i] || IGNORE_TARGET;
    if ((!prev || prev === IGNORE_TARGET) && next && next !== IGNORE_TARGET) filled += 1;
  }
  return filled;
}


/**
 * Rebuild profile.fields from the administrator-reviewed mapping.
 * Reviewed mapping is authoritative; existing profile.fields never override it.
 * Only JLR province may remain a QC constant when no province column is mapped.
 *
 * @param {object|null|undefined} profile
 * @param {string[]} mapping
 * @param {number} columnCount
 */
export function applyReviewedMappingToProfile(profile, mapping, columnCount) {
  const src = profile && typeof profile === "object" && !Array.isArray(profile) ? profile : {};
  const count = Number.isInteger(columnCount) && columnCount >= 0 ? columnCount : 0;
  const map = normalizeMappingArray(mapping, count);
  const sourceProfile = src.sourceProfile === "jlr" ? "jlr" : "generic";

  const indexByTarget = new Map();
  for (let i = 0; i < map.length; i += 1) {
    const target = map[i];
    if (!target || target === IGNORE_TARGET) continue;
    if (!VERTEX_MAPPING_FIELD_TARGETS.includes(target)) continue;
    // First wins; normalizeMappingArray already rejects duplicates via validate,
    // but if duplicates slip through, keep the first column.
    if (!indexByTarget.has(target)) indexByTarget.set(target, i);
  }

  const fields = {};
  for (const target of VERTEX_MAPPING_FIELD_TARGETS) {
    if (indexByTarget.has(target)) {
      const index = indexByTarget.get(target);
      if (Number.isInteger(index) && index >= 0 && index < count) {
        fields[target] = { kind: "column", index };
      }
      continue;
    }
    if (target === "province" && sourceProfile === "jlr") {
      fields.province = { kind: "constant", value: "QC" };
    }
  }

  const ownerGroups = sanitizeOwnerGroupsForColumnCount(src.ownerGroups, count);
  const propertyFallback = sanitizePropertyFallbackForColumnCount(
    src.propertyFallback,
    count,
    sourceProfile,
  );

  return {
    sourceProfile,
    confidence: typeof src.confidence === "number" ? src.confidence : 0,
    reasons: Array.isArray(src.reasons) ? [...src.reasons] : [],
    fields,
    ownerGroups,
    propertyFallback,
  };
}

function sanitizeOwnerGroupsForColumnCount(ownerGroups, columnCount) {
  const count = Number.isInteger(columnCount) && columnCount >= 0 ? columnCount : 0;
  if (!Array.isArray(ownerGroups)) return [];
  return ownerGroups
    .map((group) => {
      const sanitized = sanitizeOwnerGroupIndexes(group);
      if (!sanitized || typeof sanitized !== "object") return null;
      const next = {};
      for (const slot of OWNER_SLOT_ORDER) {
        const index = sanitized[slot];
        if (!Number.isInteger(index) || index < 0 || index >= count) continue;
        next[slot] = index;
      }
      return Object.keys(next).length ? next : null;
    })
    .filter(Boolean);
}

function sanitizePropertyFallbackForColumnCount(propertyFallback, columnCount, sourceProfile) {
  if (sourceProfile !== "jlr") return null;
  if (!propertyFallback || typeof propertyFallback !== "object") return null;
  const count = Number.isInteger(columnCount) && columnCount >= 0 ? columnCount : 0;
  const next = {};
  for (const [slot, index] of Object.entries(propertyFallback)) {
    if (!Number.isInteger(index) || index < 0 || index >= count) continue;
    next[slot] = index;
  }
  return Object.keys(next).length ? next : null;
}

function ownerGroupIndexIdentity(group) {
  return OWNER_SLOT_ORDER.filter((slot) => Number.isInteger(group?.[slot]))
    .map((slot) => `${slot}:${group[slot]}`)
    .join("|");
}

function ownerGroupIndexSet(group) {
  const indexes = new Set();
  for (const slot of OWNER_SLOT_ORDER) {
    if (Number.isInteger(group?.[slot])) indexes.add(group[slot]);
  }
  return indexes;
}

function ownerGroupHasNameSignal(group) {
  return Number.isInteger(group?.lastName) || Number.isInteger(group?.firstName);
}

function ownerGroupsConflictOnKnownSlots(existing, suggestion) {
  for (const slot of OWNER_SLOT_ORDER) {
    if (
      Number.isInteger(existing?.[slot]) &&
      Number.isInteger(suggestion?.[slot]) &&
      existing[slot] !== suggestion[slot]
    ) {
      return true;
    }
  }
  return false;
}

function collectReservedPropertyIndexes(mapping, propertyFallback) {
  const reserved = new Set();
  const map = Array.isArray(mapping) ? mapping : [];
  for (let i = 0; i < map.length; i += 1) {
    const target = map[i];
    if (!target || target === IGNORE_TARGET) continue;
    if (target === "ownerName" || target === "ownerMailingAddress") continue;
    reserved.add(i);
  }
  if (propertyFallback && typeof propertyFallback === "object") {
    for (const index of Object.values(propertyFallback)) {
      if (Number.isInteger(index)) reserved.add(index);
    }
  }
  return reserved;
}

function ownerGroupOverlapsIndexes(group, reserved) {
  for (const index of ownerGroupIndexSet(group)) {
    if (reserved.has(index)) return true;
  }
  return false;
}

function mergeOwnerGroupsPreserveExisting(currentGroups, aiGroups, context = {}) {
  const columnCount = Number.isInteger(context.columnCount) ? context.columnCount : Number.POSITIVE_INFINITY;
  const sanitize = (group) => {
    const sanitized = sanitizeOwnerGroupIndexes(group);
    if (!sanitized || typeof sanitized !== "object") return null;
    const next = {};
    for (const slot of OWNER_SLOT_ORDER) {
      const index = sanitized[slot];
      if (!Number.isInteger(index)) continue;
      if (!Number.isFinite(columnCount)) {
        next[slot] = index;
        continue;
      }
      if (index < 0 || index >= columnCount) continue;
      next[slot] = index;
    }
    return Object.keys(next).length ? next : null;
  };

  const current = Array.isArray(currentGroups) ? currentGroups.map(sanitize).filter(Boolean) : [];
  const ai = Array.isArray(aiGroups) ? aiGroups.map(sanitize).filter(Boolean) : [];
  if (!ai.length) return current;

  const propertyReserved = collectReservedPropertyIndexes(context.mapping, context.propertyFallback);

  const acceptAppendedGroup = (group, accepted) => {
    if (!ownerGroupHasNameSignal(group)) return false;
    if (ownerGroupOverlapsIndexes(group, propertyReserved)) return false;
    const reservedOwners = new Set();
    for (const existing of [...current, ...accepted]) {
      for (const index of ownerGroupIndexSet(existing)) reservedOwners.add(index);
    }
    if (ownerGroupOverlapsIndexes(group, reservedOwners)) return false;
    const identity = ownerGroupIndexIdentity(group);
    if (!identity) return false;
    if ([...current, ...accepted].some((existing) => ownerGroupIndexIdentity(existing) === identity)) {
      return false;
    }
    return true;
  };

  if (!current.length) {
    const accepted = [];
    for (const group of ai) {
      if (acceptAppendedGroup(group, accepted)) accepted.push(group);
    }
    return accepted;
  }

  const merged = current.map((group, index) => {
    const suggestion = ai[index];
    if (!suggestion) return group;
    if (ownerGroupsConflictOnKnownSlots(group, suggestion)) return group;

    const reserved = new Set(propertyReserved);
    for (let i = 0; i < current.length; i += 1) {
      if (i === index) continue;
      for (const idx of ownerGroupIndexSet(current[i])) reserved.add(idx);
    }
    for (const idx of ownerGroupIndexSet(group)) reserved.add(idx);

    const next = { ...group };
    for (const slot of OWNER_SLOT_ORDER) {
      if (Number.isInteger(next[slot])) continue;
      if (!Number.isInteger(suggestion[slot])) continue;
      if (reserved.has(suggestion[slot])) continue;
      next[slot] = suggestion[slot];
      reserved.add(suggestion[slot]);
    }
    return sanitize(next) || group;
  });

  const acceptedExtra = [];
  if (ai.length > current.length) {
    for (let i = current.length; i < ai.length; i += 1) {
      if (ai[i] && acceptAppendedGroup(ai[i], [...merged, ...acceptedExtra])) {
        acceptedExtra.push(ai[i]);
      }
    }
  }
  return [...merged, ...acceptedExtra];
}

/**
 * Non-destructive merge of an advisory Vertex suggestion into the current setup.
 * Never clears existing valid mappings, profile.fields, or owner groups.
 * For JLR, never downgrades sourceProfile to generic.
 */
export function mergeVertexSuggestionIntoSetup(suggestion, headers, currentProfile, currentMapping) {
  const list = Array.isArray(headers) ? headers : [];
  const columnCount = list.length;
  const beforeMapping = normalizeMappingArray(currentMapping, columnCount);
  const mapping = [...beforeMapping];
  const currentFields =
    currentProfile?.fields && typeof currentProfile.fields === "object" ? { ...currentProfile.fields } : {};
  const nextFields = { ...currentFields };
  const suggestionFields =
    suggestion?.fields && typeof suggestion.fields === "object" ? suggestion.fields : {};
  const keepJlr = currentProfile?.sourceProfile === "jlr";

  for (const target of VERTEX_MAPPING_FIELD_TARGETS) {
    const aiField = suggestionFields[target];
    if (!aiField || aiField.kind === "unmapped") continue;

    const alreadyMappedTarget =
      isMappedField(currentFields[target]) || mapping.some((value) => value === target);
    if (alreadyMappedTarget) continue;

    if (aiField.kind === "constant") {
      const value = typeof aiField.value === "string" ? aiField.value.trim() : "";
      if (!value) continue;
      nextFields[target] = { kind: "constant", value };
      continue;
    }

    if (aiField.kind !== "column" || !Number.isInteger(aiField.index)) continue;
    const index = aiField.index;
    if (index < 0 || index >= columnCount) continue;

    const occupied = mapping[index];
    if (occupied && occupied !== IGNORE_TARGET && occupied !== target) {
      // Conflicting column assignment — skip, do not overwrite.
      continue;
    }

    mapping[index] = target;
    nextFields[target] = { kind: "column", index };
  }

  const nextOwnerGroups = mergeOwnerGroupsPreserveExisting(
    currentProfile?.ownerGroups,
    suggestion?.ownerGroups,
    {
      columnCount,
      mapping,
      propertyFallback: currentProfile?.propertyFallback,
    },
  );

  if (ownerGroupHasAddressSlots(nextOwnerGroups[0])) {
    for (let i = 0; i < mapping.length; i += 1) {
      if (mapping[i] === "ownerMailingAddress") mapping[i] = IGNORE_TARGET;
    }
  }

  const sourceProfile = keepJlr
    ? "jlr"
    : suggestion?.sourceProfile === "jlr"
      ? "jlr"
      : currentProfile?.sourceProfile === "jlr"
        ? "jlr"
        : "generic";

  const profile = applyReviewedMappingToProfile(
    {
      sourceProfile,
      confidence:
        keepJlr && typeof currentProfile?.confidence === "number"
          ? currentProfile.confidence
          : typeof suggestion?.confidence === "number"
            ? suggestion.confidence
            : currentProfile?.confidence || 0,
      reasons: [...(currentProfile?.reasons || []), "vertex_suggestion"],
      ownerGroups: nextOwnerGroups,
      propertyFallback:
        sourceProfile === "jlr" ? currentProfile?.propertyFallback || null : currentProfile?.propertyFallback || null,
    },
    mapping,
    columnCount,
  );

  const before = { mapping: beforeMapping, profile: currentProfile };
  const after = { mapping, profile };
  const filledCount = countFilledEmptyMappings(beforeMapping, mapping);
  return {
    mapping,
    profile,
    filledCount,
    changeCount: countMappingConfigChanges(before, after),
  };
}

/** @deprecated Use mergeVertexSuggestionIntoSetup — kept as the apply entry point. */
export function applyVertexSuggestionToSetup(suggestion, headers, currentProfile, currentMapping) {
  return mergeVertexSuggestionIntoSetup(suggestion, headers, currentProfile, currentMapping);
}

export { IGNORE_TARGET };
